import { DIV, H2 } from 'sdi/components/elements';
import { stringToParagraphs } from 'sdi/util';
import tr, { Translated, toTranslated } from 'sdi/locale';

import { setLayout } from 'view/src/events/app';

import { makeLabelAndIcon } from '../button';
import {
    HARVEST_RADIUS,
    getGeometry,
    getGeometryType,
    getHarvestRadius,
} from 'view/src/queries/harvest';
import {
    startDrawing,
    endHarvest,
    selectHarvestRadius,
} from 'view/src/events/harvest';
import { makeLabel } from 'sdi/components/button';
import { GeometryType, IMapInfo } from 'sdi/source';
import { renderMapData } from '../legend';
import { renderRelated } from '../related-maps';

/**
  User interaction

  1. startDrawing(geometryType) 
    - user picks point/line/polygon;
    - sets the map interaction to create mode so OpenLayers captures
   the drawn shape.
  2. When drawing completes, addFeature(f) is called with the resulting GeoJSON feature.

  Firing requests

  endDrawing resets the map interaction, then iterates over all visible layers 
  of the current map and calls request(mapId, metadataId, id, groupId) for each.
  request posts a request on /api/harvest

  Display

  addHarvestToMap injects the filtered features back as a new map layer so they render
  on the map. The table view is also switched to show the harvested features.
*/
const buttonCancel = makeLabelAndIcon('cancel', 2, 'times', () =>
    tr.view('clearFilter'),
);

const buttonQueryPage = makeLabelAndIcon('navigate', 1, 'file', () =>
    tr.view('goToQueryPage'),
);

const harvestPointButton = makeLabel('info', 2, () =>
    tr.view('harvestInitiatePoint'),
);
const harvestLineButton = makeLabel('info', 2, () =>
    tr.view('harvestInitiateLine'),
);
const harvestPolygonButton = makeLabel('info', 2, () =>
    tr.view('harvestInitiatePolygon'),
);

const renderTitle = () => H2({}, tr.view('harvestTitle'));

const wrapInfo = (t: Translated) => DIV({}, stringToParagraphs(t));

const renderInfo = () => {
    const gt = getGeometryType().getOrElse('Polygon');
    switch (gt) {
        case 'Point':
        case 'MultiPoint':
            return wrapInfo(tr.view('harvestInfoPoint'));
        case 'LineString':
        case 'MultiLineString':
            return wrapInfo(tr.view('harvestInfoLine'));
        case 'Polygon':
        case 'MultiPolygon':
            return wrapInfo(tr.view('harvestInfoPolygon'));
    }
};

const pointRadiusButton = (radius: number) =>
    makeLabelAndIcon('select', 2, 'arrows-h', () =>
        radius < 1000
            ? toTranslated(`${radius} m`)
            : toTranslated(`${radius / 1000} km`),
    );

const renderPointOption = () =>
    DIV(
        'filter-options__wrapper',
        DIV('', tr.view('harvestInfoPointOption')),
        HARVEST_RADIUS.map(radius =>
            pointRadiusButton(radius)(
                () => selectHarvestRadius(radius),
                getHarvestRadius().getOrElse(0) === radius ? 'active' : '',
            ),
        ),
    );

const initiateHarvest = (gt: GeometryType) => {
    startDrawing(gt);
};

const renderInitial = () =>
    DIV(
        'sidebar-main tool spatial-filter',
        H2({}, tr.view('harvestTitle')),
        DIV('', tr.view('spatialFilterHelpText')),
        DIV(
            'filter-selection__wrapper ',
            harvestPointButton(() => initiateHarvest('Point')),
            harvestLineButton(() => initiateHarvest('LineString')),
            harvestPolygonButton(() => initiateHarvest('Polygon')),
        ),
    );

const renderRunning = (mapInfo: IMapInfo) =>
    DIV(
        'sidebar-main tool spatial-filter activated',
        renderTitle(),
        renderInfo(),
        getGeometryType().map(geometryType =>
            geometryType === 'Point' ? renderPointOption() : '',
        ),
        DIV(
            'filter-action__wrapper',
            getGeometry().map(() => buttonQueryPage(() => setLayout('query'))),
            buttonCancel(endHarvest),
        ),
        getGeometry().map(() =>
            DIV('', renderMapData(mapInfo, false), renderRelated()),
        ),
    );

export const renderHarvest = (mapInfo: IMapInfo) =>
    getGeometryType().foldL(renderInitial, () => renderRunning(mapInfo));

export default renderHarvest;
